import type { WallCard } from '@walls/store';
import { getCardRect } from './cardRects';
import { getRelativeCards } from './relativeCards';
import type { RelativeCard } from './types';

export interface PositionUpdate {
  cardId: string;
  xPos: number;
  yPos: number;
}

const autoAlignNearbyDistance = 50;
const autoAlignSnapDistance = 10;
const autoAlignEdgeDistance = 20;
export const autoAlignGap = 5;

export function positionRelativeToDrop(cards: WallCard[], droppedCard: WallCard): PositionUpdate[] {
  const droppedRect = getCardRect(droppedCard);

  if (!droppedRect) {
    return [];
  }

  const nearbyCards = cards.filter((card) => {
    if (card.id === droppedCard.id) {
      return false;
    }

    const rect = getCardRect(card);
    return rect ? isCardWithinRange(autoAlignNearbyDistance, droppedRect, rect) : false;
  });

  if (nearbyCards.length === 0) {
    return [];
  }

  let minX: number | null = null;
  let minY: number | null = null;
  let snapToX: WallCard | null = null;
  let snapToY: WallCard | null = null;
  let nextX: number | null = null;
  let nextY: number | null = null;

  for (const nearbyCard of nearbyCards) {
    const nearbyRect = getCardRect(nearbyCard);

    if (!nearbyRect) {
      continue;
    }

    if (rectsOverlap(droppedRect, nearbyRect) && !isNearExternalSnapEdge(droppedRect, nearbyRect)) {
      continue;
    }

    const distanceX = Math.abs(nearbyRect.x - droppedRect.x);
    const distanceY = Math.abs(nearbyRect.y - droppedRect.y);

    if (minX === null || distanceX < minX) {
      snapToX = nearbyCard;
      minX = distanceX;
      nextX = nearbyRect.x;
    }

    if (minY === null || distanceY < minY) {
      snapToY = nearbyCard;
      minY = distanceY;
      nextY = nearbyRect.y;
    }
  }

  const snappedPosition = {
    xPos: droppedRect.x,
    yPos: droppedRect.y,
  };
  let snappedToX = false;

  if (minX !== null && nextX !== null && snapToX && minX < autoAlignSnapDistance) {
    const snapRect = getCardRect(snapToX);

    if (snapRect && isCardWithinRange(autoAlignEdgeDistance, droppedRect, snapRect)) {
      snappedPosition.yPos =
        snapRect.y + snapRect.height / 2 < droppedRect.y
          ? snapRect.y + snapRect.height + autoAlignGap
          : snapRect.y - droppedRect.height - autoAlignGap;
    }

    snappedPosition.xPos = nextX;
    snappedToX = true;
  }

  if (!snappedToX && minY !== null && nextY !== null && snapToY && minY < autoAlignSnapDistance) {
    const snapRect = getCardRect(snapToY);

    if (snapRect && isCardWithinRange(autoAlignEdgeDistance, droppedRect, snapRect)) {
      snappedPosition.xPos =
        snapRect.x + snapRect.width / 2 < droppedRect.x
          ? snapRect.x + snapRect.width + autoAlignGap
          : snapRect.x - droppedRect.width - autoAlignGap;
    }

    snappedPosition.yPos = nextY;
  }

  if (snappedPosition.xPos === droppedRect.x && snappedPosition.yPos === droppedRect.y) {
    return [];
  }

  return [
    {
      cardId: droppedCard.id,
      xPos: Math.round(snappedPosition.xPos),
      yPos: Math.round(snappedPosition.yPos),
    },
  ];
}

export function positionRelativeToResize(
  cards: WallCard[],
  previousRect: { height: number; width: number; x: number; y: number },
  resizedCard: WallCard
) {
  const nextRect = getCardRect(resizedCard);

  if (!nextRect) {
    return [];
  }

  const updates: PositionUpdate[] = [];
  const movedPositions = new Map<string, { x: number; y: number }>();

  if (previousRect.width !== nextRect.width) {
    const seedCard = findResizeSeedCard(cards, resizedCard, nextRect, previousRect.width, 'x');

    if (seedCard) {
      const relativeCards = getRelativeCards(cards, seedCard, [resizedCard.id, seedCard.id]).filter(
        ({ card }) => {
          const rect = getCardRect(card);
          return rect ? rect.x > nextRect.x + previousRect.width : false;
        }
      );

      const seedUpdate = {
        cardId: seedCard.id,
        xPos: nextRect.x + nextRect.width + 5,
        yPos: nextRect.y,
      };
      updates.push(seedUpdate);
      movedPositions.set(seedCard.id, { x: seedUpdate.xPos, y: seedUpdate.yPos });
      updates.push(...positionChildCards(seedCard, relativeCards, movedPositions));
    }
  }

  if (previousRect.height !== nextRect.height) {
    const seedCard = findResizeSeedCard(cards, resizedCard, nextRect, previousRect.height, 'y');

    if (seedCard) {
      const relativeCards = getRelativeCards(cards, seedCard, [resizedCard.id, seedCard.id]).filter(
        ({ card }) => {
          const rect = getCardRect(card);
          return rect ? rect.y > nextRect.y + previousRect.height : false;
        }
      );

      const seedUpdate = {
        cardId: seedCard.id,
        xPos: nextRect.x,
        yPos: nextRect.y + nextRect.height + 5,
      };
      updates.push(seedUpdate);
      movedPositions.set(seedCard.id, { x: seedUpdate.xPos, y: seedUpdate.yPos });
      updates.push(...positionChildCards(seedCard, relativeCards, movedPositions));
    }
  }

  return updates;
}

function positionChildCards(
  parentCard: WallCard,
  relativeCards: RelativeCard[],
  movedPositions: Map<string, { x: number; y: number }>
): PositionUpdate[] {
  const updates: PositionUpdate[] = [];
  const childCards = relativeCards.filter(({ parentId }) => parentId === parentCard.id);
  const parentRect = getMovedCardRect(parentCard, movedPositions);

  if (!parentRect) {
    return updates;
  }

  for (const childCard of childCards) {
    const childRect = getCardRect(childCard.card);

    if (!childRect) {
      continue;
    }

    switch (childCard.position) {
      case 'n':
        updates.push({
          cardId: childCard.card.id,
          xPos: parentRect.x,
          yPos: parentRect.y - childRect.height - 5,
        });
        break;
      case 's':
        updates.push({
          cardId: childCard.card.id,
          xPos: parentRect.x,
          yPos: parentRect.y + parentRect.height + 5,
        });
        break;
      case 'e':
        updates.push({
          cardId: childCard.card.id,
          xPos: parentRect.x + parentRect.width + 5,
          yPos: parentRect.y,
        });
        break;
      case 'w':
        updates.push({
          cardId: childCard.card.id,
          xPos: parentRect.x - childRect.width - 5,
          yPos: parentRect.y,
        });
        break;
    }

    const latestUpdate = updates[updates.length - 1];
    movedPositions.set(childCard.card.id, { x: latestUpdate.xPos, y: latestUpdate.yPos });
    updates.push(...positionChildCards(childCard.card, relativeCards, movedPositions));
  }

  return updates;
}

function getMovedCardRect(card: WallCard, movedPositions: Map<string, { x: number; y: number }>) {
  const rect = getCardRect(card);
  const movedPosition = movedPositions.get(card.id);

  if (!rect || !movedPosition) {
    return rect;
  }

  return {
    ...rect,
    x: movedPosition.x,
    y: movedPosition.y,
  };
}

function findResizeSeedCard(
  cards: WallCard[],
  resizedCard: WallCard,
  nextRect: { height: number; width: number; x: number; y: number },
  previousSize: number,
  axis: 'x' | 'y'
) {
  const isXAxis = axis === 'x';
  const candidates = cards
    .filter((card) => {
      const rect = getCardRect(card);

      if (!rect || card.id === resizedCard.id || card.isLocked) {
        return false;
      }

      return isXAxis
        ? Math.floor(rect.y) > Math.floor(nextRect.y) - 2 && Math.floor(rect.y) < Math.ceil(nextRect.y) + 2
        : Math.floor(rect.x) > Math.floor(nextRect.x) - 2 && Math.floor(rect.x) < Math.ceil(nextRect.x) + 2;
    })
    .sort((a, b) => {
      const aRect = getCardRect(a);
      const bRect = getCardRect(b);

      if (!aRect || !bRect) return 0;
      return isXAxis ? aRect.x - bRect.x : aRect.y - bRect.y;
    });

  for (const candidate of candidates) {
    const rect = getCardRect(candidate);

    if (!rect) {
      continue;
    }

    const candidateStart = isXAxis ? rect.x : rect.y;
    const previousEnd = (isXAxis ? nextRect.x : nextRect.y) + previousSize;

    if (candidateStart > previousEnd && Math.floor(candidateStart - previousEnd) <= 6) {
      return candidate;
    }
  }

  return null;
}

function isCardWithinRange(
  distance: number,
  sourceRect: { height: number; width: number; x: number; y: number },
  targetRect: { height: number; width: number; x: number; y: number }
) {
  const sourceEndX = sourceRect.x + sourceRect.width;
  const sourceEndY = sourceRect.y + sourceRect.height;
  const targetEndX = targetRect.x + targetRect.width;
  const targetEndY = targetRect.y + targetRect.height;

  const xClose =
    (sourceRect.x > targetEndX && sourceRect.x - targetEndX < distance) ||
    (targetRect.x > sourceEndX && targetRect.x - sourceEndX < distance) ||
    (sourceRect.x > targetRect.x && sourceRect.x < targetEndX) ||
    (sourceEndX > targetRect.x && sourceEndX < targetEndX) ||
    (targetRect.x > sourceRect.x && targetRect.x < sourceEndX) ||
    (targetEndX > sourceRect.x && targetEndX < sourceEndX);

  const yClose =
    (sourceRect.y > targetEndY && sourceRect.y - targetEndY < distance) ||
    (targetRect.y > sourceEndY && targetRect.y - sourceEndY < distance) ||
    (sourceRect.y > targetRect.y && sourceRect.y < targetEndY) ||
    (sourceEndY > targetRect.y && sourceEndY < targetEndY) ||
    (targetRect.y > sourceRect.y && targetRect.y < sourceEndY) ||
    (targetEndY > sourceRect.y && targetEndY < sourceEndY);

  return xClose && yClose;
}

function rectsOverlap(
  sourceRect: { height: number; width: number; x: number; y: number },
  targetRect: { height: number; width: number; x: number; y: number }
) {
  return (
    targetRect.x < sourceRect.x + sourceRect.width &&
    targetRect.x + targetRect.width > sourceRect.x &&
    sourceRect.y + sourceRect.height > targetRect.y &&
    sourceRect.y < targetRect.y + targetRect.height
  );
}

function isNearExternalSnapEdge(
  sourceRect: { height: number; width: number; x: number; y: number },
  targetRect: { height: number; width: number; x: number; y: number }
) {
  const sourceEndX = sourceRect.x + sourceRect.width;
  const sourceEndY = sourceRect.y + sourceRect.height;
  const targetEndX = targetRect.x + targetRect.width;
  const targetEndY = targetRect.y + targetRect.height;

  return (
    sourceRect.x >= targetEndX - autoAlignEdgeDistance ||
    targetRect.x >= sourceEndX - autoAlignEdgeDistance ||
    sourceRect.y >= targetEndY - autoAlignEdgeDistance ||
    targetRect.y >= sourceEndY - autoAlignEdgeDistance
  );
}
